import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ShoppingCart,
    Plus,
    Minus,
    Trash2,
    Package,
    Wrench,
    CheckCircle,
    Loader2,
    AlertCircle,
    Receipt,
    Wallet
} from 'lucide-react';
import { useToast } from '../hooks/useToast';
import Card from '../components/ui/Card';
import {
    fetchInventoryForStoreSales,
    fetchServicesForStoreSale,
    currencyCodeToSymbolMap,
    fetchTotalReceivableBalance,
    fetchCustomerDepositBalance,
    fetchDiscountRules,
} from "../apiHelper";

const NewPayments = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Safely pull state parameters from navigation
    const { ward } = location.state || {};

    // console.log(ward)

    const [user, setUser] = useState(null);
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    // Financial metrics
    const [amountOwed, setAmountOwed] = useState(0);
    const [availableDeposit, setAvailableDeposit] = useState(0);
    const [loadingFinances, setLoadingFinances] = useState(true);

    const [searchQuery, setSearchQuery] = useState("");

    const [discountRules, setDiscountRules] = useState([]);
    const [cartDiscounts, setCartDiscounts] = useState({});

    // Safeguard route authentication and retrieve user profile
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser || !ward) {
            showToast("Missing authentication context or ward data.", "error");
            navigate('/dashboard');
            return;
        }
        setUser(JSON.parse(lcUser));
    }, [ward, navigate, showToast]);

    // Fetch up-to-date ward financial status balances
    useEffect(() => {
        if (!ward) return;

        const fetchFinancialStatus = async () => {
            setLoadingFinances(true);
            try {
                const [receivableRes, depositRes] = await Promise.all([
                    fetchTotalReceivableBalance({ customer_id: ward.member_id }),
                    fetchCustomerDepositBalance(ward.member_id)
                ]);

                // Normalize receivable balance
                let numericBalance = 0;
                if (typeof receivableRes === 'number') numericBalance = receivableRes;
                else if (receivableRes && typeof receivableRes === 'object') {
                    numericBalance = receivableRes.total_receivable ?? receivableRes.balance ?? receivableRes.amount ?? 0;
                }
                setAmountOwed(parseFloat(numericBalance) || 0);

                // Normalize deposit balance
                let numericDeposit = 0;
                if (typeof depositRes === 'number') numericDeposit = depositRes;
                else if (depositRes && typeof depositRes === 'object') {
                    numericDeposit = depositRes.deposit_balance ?? depositRes.balance ?? depositRes.amount ?? 0;
                }
                setAvailableDeposit(parseFloat(numericDeposit) || 0);

            } catch (err) {
                console.error("Error fetching financial status:", err);
                showToast("Failed to refresh ward balance information.", "warning");
            } finally {
                setLoadingFinances(false);
            }
        };

        fetchFinancialStatus();
    }, [ward, showToast]);

    // Fetch Products and Services
    useEffect(() => {
        if (!user || !ward) return;

        const fetchStoreCatalog = async () => {
            setLoadingProducts(true);
            try {
                // Extract store_id dynamically from the ward's branch listings
                const selectedStore = ward.branches?.[0];
                if (!selectedStore?.store_id) {
                    showToast("No branch/store allocation found for this student.", "error");
                    return;
                }

                const payload = {
                    users_id: user.users_id,
                    logon_id: user.logon_id,
                    hpwc: user.hpwc,
                    store_id: selectedStore.store_id,
                    offset: 0,
                    limit: 10000 // Adjustable limit thresholds
                };

                const [fetchedInventoryLots, fetchedServicesItems] = await Promise.all([
                    fetchInventoryForStoreSales(payload),
                    fetchServicesForStoreSale(payload)
                ]);

                const allLots = [...(fetchedInventoryLots || []), ...(fetchedServicesItems || [])];
                const aggregatedProductsMap = new Map();

                allLots.forEach(lot => {
                    if (!lot.catentry) return;
                    const catentryId = lot.catentry.catentry_id;

                    if (!aggregatedProductsMap.has(catentryId)) {
                        const isServiceFromCatalog = lot.catentry.catgpenrels?.[0]?.catalog?.identifier?.toLowerCase().includes("service");
                        const isServiceFromLot = lot.lot_id === 0 && lot.current_quantity === 0;
                        const isService = isServiceFromCatalog || isServiceFromLot;

                        aggregatedProductsMap.set(catentryId, {
                            id: catentryId,
                            name: lot.catentry.productname,
                            description: lot.catentry.partnumber || lot.catentry.productname,
                            price: parseFloat(lot.catentry.listprice?.listprice || 0),
                            priceSymbol: lot.catentry.listprice?.currency_rel?.setcnote || "₦",
                            currencyCode: lot.catentry.listprice?.currency_rel?.setccurr || "NGN",
                            stock: 0,
                            category: lot.catentry.catgpenrels?.[0]?.category?.identifier || "Uncategorized",
                            isService: isService,
                        });
                    }

                    const currentProduct = aggregatedProductsMap.get(catentryId);
                    // Add physical stock inventory quantities incrementally
                    currentProduct.stock += (lot.current_quantity || 0);
                });

                setProducts(Array.from(aggregatedProductsMap.values()));
            } catch (err) {
                console.error("Error loading catalogue data:", err);
                showToast("Could not load available products and services.", "error");
            } finally {
                setLoadingProducts(false);
            }
        };

        fetchStoreCatalog();
    }, [user, ward, showToast]);


    // Fetch Discount Rules
    useEffect(() => {
        if (!user || !ward) return;

        const getRules = async () => {
            try {
                const rules = await fetchDiscountRules({
                    ...user,
                    offset: 0,
                    limit: 10000
                });

                const selectedStore = ward.branches?.[0];
                const storeId = selectedStore?.store_id;
                const now = new Date();

                // Filter rules based on strict eligibility parameters
                const eligibleRules = (rules || []).filter(rule => {
                    if (!rule.is_active) return false;

                    // 1. Store matching rule evaluation
                    const belongsToStore = rule.stores?.some(s => s.store_id === storeId);
                    if (!belongsToStore) return false;

                    // 2. Timeline period validation
                    const start = new Date(rule.start_date);
                    const end = new Date(rule.end_date);
                    if (now < start || now > end) return false;

                    // 3. Customer target validation (if customers array isn't empty, ward must be explicitly whitelisted)
                    if (rule.customers && rule.customers.length > 0) {
                        const isTargetedWard = rule.customers.some(c => c.user?.users_id === ward.member_id || c.member_id === ward.member_id);
                        if (!isTargetedWard) return false;
                    }

                    return true;
                });

                setDiscountRules(eligibleRules);
            } catch (err) {
                console.error("Error pulling discount provisions:", err);
                // Non-breaking fallback, suppress user-facing warnings to avoid checkout blockers
                showToast(err.response?.data?.detail || "Error fetching discount rules", "error");
            }
        };

        getRules();
    }, [user, ward]);

    // Dynamic Discount Evaluator Engine
    useEffect(() => {
        const freshDiscounts = {};

        cart.forEach(item => {
            // Find matching rules targeting this product specifically
            const rulesForProduct = discountRules.filter(rule =>
                rule.products?.some(p => p.catentry_id === item.id) || rule.target_product_id === item.id
            );

            let bestDiscountAmount = 0;

            rulesForProduct.forEach(rule => {
                const itemSubtotalBeforeDiscount = item.price * item.quantity;

                // Rule constraint validation thresholds
                if (rule.minimum_purchase_quantity && item.quantity < rule.minimum_purchase_quantity) return;
                if (rule.minimum_purchase_amount && itemSubtotalBeforeDiscount < parseFloat(rule.minimum_purchase_amount)) return;

                let currentRuleDiscount = 0;

                // Evaluator branch based on rule design taxonomy types
                if (rule.discount_type === 'tiered_pricing' && rule.tiers?.length > 0) {
                    // Find applicable structural tier match matching quantity parameters
                    const applicableTier = rule.tiers.find(tier => {
                        const minMatch = item.quantity >= tier.min_quantity;
                        const maxMatch = !tier.max_quantity || item.quantity <= tier.max_quantity;
                        return minMatch && maxMatch;
                    });

                    if (applicableTier) {
                        // Tiered items yield direct per-unit price adjustment savings offsets
                        currentRuleDiscount = parseFloat(applicableTier.tier_value) * item.quantity;
                    }
                } else if (rule.discount_type === 'percentage') {
                    const percentVal = parseFloat(rule.value) || 0;
                    currentRuleDiscount = itemSubtotalBeforeDiscount * (percentVal / 100);
                } else if (rule.discount_type === 'fixed_amount') {
                    const absoluteFlatValue = parseFloat(rule.value) || 0;
                    // Fixed discounts apply per targeted catalog item transaction
                    currentRuleDiscount = absoluteFlatValue * item.quantity;
                }

                // Accumulate best pricing matrix advantage logic pattern
                if (currentRuleDiscount > bestDiscountAmount) {
                    bestDiscountAmount = currentRuleDiscount;
                }
            });

            // Cap calculated reductions so prices never drop below zero
            const theoreticalMaxSaving = item.price * item.quantity;
            if (bestDiscountAmount > theoreticalMaxSaving) bestDiscountAmount = theoreticalMaxSaving;

            if (bestDiscountAmount > 0) {
                freshDiscounts[item.id] = bestDiscountAmount;
            }
        });

        setCartDiscounts(freshDiscounts);
    }, [cart, discountRules]);

    // Cart Management functions
    const addToCart = (product) => {
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === product.id);
            if (existingItem) {
                if (!product.isService && existingItem.quantity >= product.stock) {
                    showToast(`Cannot exceed remaining stock (${product.stock})`, "warning");
                    return prevCart;
                }
                return prevCart.map(item =>
                    item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prevCart, { ...product, quantity: 1 }];
        });
    };

    const updateQuantity = (id, amount) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.id === id) {
                    const newQty = item.quantity + amount;
                    if (!item.isService && newQty > item.stock) {
                        showToast(`Requested quantity exceeds available stock.`, "warning");
                        return item;
                    }
                    return newQty > 0 ? { ...item, quantity: newQty } : null;
                }
                return item;
            }).filter(Boolean);
        });
    };

    const removeFromCart = (id) => {
        setCart(prevCart => prevCart.filter(item => item.id !== id));
    };

    const filteredProducts = products.filter(product =>
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Computations
    // const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalDiscountDeductions = Object.values(cartDiscounts).reduce((acc, currentVal) => acc + currentVal, 0);

    // Grand Total Equation = Amount Owed + New Items Subtotal - Upfront Available Deposits
    // const aggregateTotal = (amountOwed + cartSubtotal) - availableDeposit;
    // const finalAmountToPay = aggregateTotal > 0 ? aggregateTotal : 0;
    const aggregateTotal = (amountOwed + cartSubtotal - totalDiscountDeductions) - availableDeposit;
    const finalAmountToPay = aggregateTotal > 0 ? aggregateTotal : 0;

    // const handleCheckoutSubmission = () => {
    //     if (cart.length === 0 && amountOwed <= 0) {
    //         showToast("No items or outstanding balances to settle", "error");
    //         return;
    //     }
    //     const orderPayload = {
    //         ward: ward, // The complete ward object containing profile, branches, etc.
    //         amountOwed: amountOwed,
    //         availableDeposit: availableDeposit,
    //         cartItems: cart,
    //         subtotal: cartSubtotal,
    //         finalCalculatedPayment: finalAmountToPay // This becomes the default price inside MakePayment
    //     };

    //     navigate('/dashboard/payment-gateway', { state: orderPayload });
    // };

    const handleCheckoutSubmission = () => {
        if (cart.length === 0 && amountOwed <= 0) {
            showToast("No items or outstanding balances to settle", "error");
            return;
        }
        const orderPayload = {
            ward: ward,
            amountOwed: amountOwed,
            availableDeposit: availableDeposit,
            cartItems: cart.map(item => ({
                ...item,
                discountApplied: cartDiscounts[item.id] || 0 // pass discrete discount breakdown per element
            })),
            subtotal: cartSubtotal,
            totalDiscounts: totalDiscountDeductions, // pass aggregated savings metric
            finalCalculatedPayment: finalAmountToPay
        };

        navigate('/dashboard/payment-gateway', { state: orderPayload });
    };

    if (!ward) return null;

    return (
        <div className="min-h-screen bg-zinc-50/50 p-6 sm:p-8 space-y-6">
            {/* Context Navigation Ribbon */}
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900 transition-colors group cursor-pointer"
            >
                <ArrowLeft size={16} className="transform group-hover:-translate-x-0.5 transition-transform" />
                Back to Dashboard
            </button>

            {/* Header Details */}
            <header className="flex flex-col gap-1">
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider">Checkout Terminal</p>
                <h1 className="text-3xl font-extrabold text-zinc-900 tracking-tight">
                    New Payment for {ward.first_name} {ward.last_name}
                </h1>
                <p className="text-sm text-zinc-500">
                    Add catalog items or structural services to initiate ledger processing transactions.
                </p>
            </header>

            {/* Ward Ledger Quick Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-5 bg-white border-zinc-200/60 flex items-center justify-between shadow-sm">
                    <div className="space-y-1">
                        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Outstanding Amount Owed</p>
                        {loadingFinances ? (
                            <Loader2 className="w-5 h-5 text-indigo-600 animate-spin mt-1" />
                        ) : (
                            <p className="text-2xl font-black text-red-600">
                                {currencyCodeToSymbolMap['NGN']}{amountOwed.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                        )}
                    </div>
                    <div className="p-3 bg-red-50 rounded-xl text-red-600"><Receipt size={24} /></div>
                </Card>

                <Card className="p-5 bg-white border-zinc-200/60 flex items-center justify-between shadow-sm">
                    <div className="space-y-1">
                        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Available Upfront Deposit Balance</p>
                        {loadingFinances ? (
                            <Loader2 className="w-5 h-5 text-indigo-600 animate-spin mt-1" />
                        ) : (
                            <p className="text-2xl font-black text-emerald-600">
                                {currencyCodeToSymbolMap['NGN']}{availableDeposit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                        )}
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600"><Wallet size={24} /></div>
                </Card>
            </div>

            {/* Split Screen Application Body Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                {/* Catalog Listing Inventory Items */}
                <div className="lg:col-span-2 space-y-4">
                    {/* <h2 className="text-lg font-bold text-zinc-900 tracking-tight">Available Items & Services</h2> */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <h2 className="text-lg font-bold text-zinc-900 tracking-tight">Available Items & Services</h2>

                        {/* Live Search Input Field */}
                        <div className="relative w-full sm:w-64">
                            <input
                                type="text"
                                placeholder="Search items or services..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-3 py-1.5 pl-9 text-xs border border-zinc-200 rounded-lg bg-white placeholder-zinc-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 transition-all"
                            />
                            <svg
                                className="absolute left-3 top-2.5 h-3.5 w-3.5 text-zinc-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>

                    {loadingProducts ? (
                        <div className="flex flex-col items-center py-12 gap-2">
                            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                            <p className="text-sm text-zinc-500 font-medium">Syncing school store inventories...</p>
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <Card className="p-8 text-center border-dashed border-zinc-300 text-zinc-400 bg-zinc-50/50">
                            {searchQuery ? "No catalog match found for your search query" : "No functional inventory line-items or services catalogued for this school location."}
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {filteredProducts.map((product) => (
                                <Card key={product.id} className="p-4 bg-white border-zinc-200/60 flex flex-col justify-between hover:border-zinc-300 transition-all shadow-sm">
                                    <div className="space-y-2">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1.5 ${product.isService ? "bg-amber-50 text-amber-700 border border-amber-100" : "bg-blue-50 text-blue-700 border border-blue-100"
                                                    }`}>
                                                    {product.isService ? <Wrench size={10} /> : <Package size={10} />}
                                                    {product.isService ? "Service" : "Product"}
                                                </span>
                                                <h3 className="font-bold text-zinc-900 text-sm line-clamp-1">{product.name}</h3>
                                                <p className="text-xs text-zinc-400 font-mono mt-0.5 truncate">{product.description}</p>
                                            </div>
                                            <p className="font-extrabold text-zinc-900 text-sm whitespace-nowrap">
                                                {currencyCodeToSymbolMap['NGN']}{product.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="pt-4 mt-4 border-t border-zinc-100 flex items-center justify-between">
                                        <span className="text-[11px] text-zinc-400 font-medium">
                                            {product.isService ? "Continuous Scope" : `Stock remaining: ${product.stock}`}
                                        </span>
                                        <button
                                            onClick={() => addToCart(product)}
                                            disabled={!product.isService && product.stock <= 0}
                                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-100 disabled:text-zinc-400 text-white font-semibold text-xs rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                                        >
                                            <Plus size={14} /> Add to Order
                                        </button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right Column Checkout Summary Cart Container */}
                <div className="lg:col-span-1 space-y-4">
                    <h2 className="text-lg font-bold text-zinc-900 tracking-tight">Order Manifest Cart</h2>

                    <Card className="p-5 bg-white border-zinc-200/60 shadow-sm space-y-4">
                        {cart.length === 0 ? (
                            <div className="py-8 text-center text-zinc-400 flex flex-col items-center justify-center gap-2">
                                <ShoppingCart size={28} className="text-zinc-300" />
                                <p className="text-xs font-medium">Cart is currently empty.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-zinc-100 max-h-[280px] overflow-y-auto pr-1 space-y-3">
                                {cart.map((item) => (
                                    <div key={item.id} className="pt-3 first:pt-0 flex items-center justify-between gap-2">
                                        {/* <div className="space-y-0.5">
                                            <h4 className="font-bold text-zinc-800 text-xs line-clamp-1">{item.name}</h4>
                                            <p className="text-[11px] text-zinc-500 font-medium">
                                                {currencyCodeToSymbolMap['NGN']}{(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </p>
                                        </div> */}
                                        <div className="space-y-0.5">
                                            <h4 className="font-bold text-zinc-800 text-xs line-clamp-1">{item.name}</h4>
                                            <p className="text-[11px] text-zinc-500 font-medium">
                                                {cartDiscounts[item.id] ? (
                                                    <span className="flex flex-col">
                                                        <span className="line-through text-zinc-400 mr-1">
                                                            {currencyCodeToSymbolMap['NGN']}{(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </span>
                                                        <span className="text-emerald-600 font-bold">
                                                            {currencyCodeToSymbolMap['NGN']}{((item.price * item.quantity) - cartDiscounts[item.id]).toLocaleString(undefined, { minimumFractionDigits: 2 })} Saved!
                                                        </span>
                                                    </span>
                                                ) : (
                                                    `${currencyCodeToSymbolMap['NGN']}${(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                                )}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex items-center border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50">
                                                <button onClick={() => updateQuantity(item.id, -1)} className="p-1 text-zinc-500 hover:bg-zinc-200 cursor-pointer"><Minus size={12} /></button>
                                                <span className="px-2 text-xs font-bold text-zinc-700 font-mono">{item.quantity}</span>
                                                <button onClick={() => updateQuantity(item.id, 1)} className="p-1 text-zinc-500 hover:bg-zinc-200 cursor-pointer"><Plus size={12} /></button>
                                            </div>
                                            <button onClick={() => removeFromCart(item.id)} className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors cursor-pointer"><Trash2 size={14} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Breakdown Financial Formulations */}
                        <div className="pt-4 border-t border-zinc-100 space-y-2 text-xs font-medium text-zinc-600">
                            <div className="flex justify-between">
                                <span>Outstanding Arrears:</span>
                                <span className="font-mono text-zinc-900">{currencyCodeToSymbolMap['NGN']}{amountOwed.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>New Additions Subtotal:</span>
                                <span className="font-mono text-zinc-900">{currencyCodeToSymbolMap['NGN']}{cartSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            {totalDiscountDeductions > 0 && (
                                <div className="flex justify-between text-emerald-600">
                                    <span>Campaign Discount Reductions:</span>
                                    <span className="font-mono">- {currencyCodeToSymbolMap['NGN']}{totalDiscountDeductions.toLocaleString(undefined, { minimumFractionDigits : 2 })}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-emerald-600">
                                <span>Deposit Deductions:</span>
                                <span className="font-mono">- {currencyCodeToSymbolMap['NGN']}{availableDeposit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>

                            {/* Total Balance Adjustment Statement */}
                            <div className="pt-3 border-t border-zinc-200 flex flex-col gap-1">
                                <div className="flex justify-between items-baseline">
                                    <span className="text-sm font-bold text-zinc-900">Final Balance Due:</span>
                                    <span className="text-xl font-black text-zinc-900 font-mono">
                                        {currencyCodeToSymbolMap['NGN']}{finalAmountToPay.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                                {aggregateTotal < 0 && (
                                    <p className="text-[10px] text-emerald-600 italic mt-1 font-semibold flex items-center gap-1">
                                        <AlertCircle size={12} /> Surplus structural deposit accounts cover full expenses remaining!
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Order Executing CTA */}
                        <button
                            onClick={handleCheckoutSubmission}
                            disabled={cart.length === 0 && amountOwed <= 0}
                            className="w-full py-2.5 mt-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-100 disabled:text-zinc-400 text-white font-bold text-sm rounded-xl shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-2"
                        >
                            <CheckCircle size={16} /> Process Settlement & Order
                        </button>
                    </Card>
                </div>

            </div>
        </div>
    );
};

export default NewPayments;